import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Upload, CheckCircle, AlertCircle, Camera, Dog, User, MapPin, MessageSquare } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api/kennel";

const KennelReviewForm = () => {
  const { token } = useParams();
  const [valid, setValid] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  
  const [formData, setFormData] = useState({
    dog_name: "",
    litter_name: "",
    owner_name: "",
    owner_city: "",
    rating: 5,
    text: "",
    image_base64: ""
  });
  
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    fetch(`${API}/review-token/${token}`)
      .then(r => r.json())
      .then(data => {
        if (data.valid) {
          setValid(true);
          if (data.customer_name) {
            setCustomerName(data.customer_name);
            setFormData(prev => ({ ...prev, owner_name: data.customer_name }));
          }
        } else {
          setValid(false);
        }
      })
      .catch(() => setValid(false))
      .finally(() => setLoading(false));
  }, [token]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError("Zdjęcie jest za duże (max 10MB)");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, image_base64: reader.result }));
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!formData.dog_name || !formData.owner_name || !formData.text) {
      setError("Wypełnij wszystkie wymagane pola");
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/reviews`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, token })
      });
      const data = await res.json();
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError(data.detail || "Wystąpił błąd");
      }
    } catch (err) {
      setError("Błąd połączenia");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0B0D] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (valid === false) {
    return (
      <div className="min-h-screen bg-[#0B0B0D] flex items-center justify-center px-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-md"
        >
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-serif text-[#F5F5F5] mb-3">Link wygasł</h1>
          <p className="text-[#B8B8B8] mb-6">Ten link do dodania opinii jest już nieaktywny lub został już wykorzystany.</p>
          <Link to="/" className="text-[#D4AF37] hover:underline">Wróć do strony głównej</Link>
        </motion.div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0B0B0D] flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }} 
            animate={{ opacity: 1, scale: 1 }}
            className="text-center max-w-md"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
            >
              <CheckCircle className="w-20 h-20 text-[#D4AF37] mx-auto mb-6" />
            </motion.div>
            <h1 className="text-3xl font-serif text-[#F5F5F5] mb-3">Dziękujemy!</h1>
            <p className="text-[#B8B8B8] mb-6">Twoja opinia została wysłana i pojawi się na stronie po weryfikacji.</p>
            
            {/* Google-style thank you card */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-[#141416] border border-[#2A2A2D] p-6 mb-8 text-left"
            >
              <div className="flex items-center gap-4 mb-4">
                <img src="/logo_katzenberg_new.png" alt="" className="w-16 h-16 object-contain" />
                <div>
                  <p className="text-[#F5F5F5] font-medium text-lg">Wolfe vom Katzenberg</p>
                  <p className="text-[#B8B8B8]/60 text-xs">Hodowla Owczarka Niemieckiego</p>
                </div>
              </div>
              <p className="text-[#B8B8B8] text-sm italic leading-relaxed">
                „Dziękujemy za podzielenie się opinią! Cieszymy się, że {formData.dog_name || "Twój pies"} odnalazł się w nowym domu. 
                Jesteśmy wdzięczni za zaufanie i życzymy wielu wspólnych radości! 🐾"
              </p>
              <p className="text-[#D4AF37] text-sm mt-4 font-medium">— Marcin & Martyna, Wolfe vom Katzenberg</p>
            </motion.div>

            <Link 
              to="/" 
              className="inline-block bg-[#D4AF37] text-[#0B0B0D] px-8 py-3 font-medium hover:bg-[#C4A030] transition-colors"
            >
              Wróć do strony
            </Link>
          </motion.div>
        </div>
        
        {/* Footer */}
        <footer className="py-4 text-center border-t border-[#2A2A2D]/30">
          <p className="text-[10px] text-[#B8B8B8]/40">
            Made with love <a href="https://apisdesign.pl" target="_blank" rel="noopener noreferrer" className="hover:text-[#D4AF37] transition-colors">apisdesign.pl</a>
          </p>
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0D] py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <Link to="/">
            <img src="/logo_katzenberg_new.png" alt="Wolfe vom Katzenberg" className="h-16 mx-auto mb-6 drop-shadow-[0_0_15px_rgba(212,175,55,0.3)]" />
          </Link>
          <h1 className="text-2xl md:text-3xl font-serif text-[#F5F5F5] mb-2">Podziel się opinią</h1>
          <p className="text-[#B8B8B8] text-sm">Dziękujemy, że zaufałeś naszej hodowli</p>
        </motion.div>

        {/* Form */}
        <motion.form 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          onSubmit={handleSubmit}
          className="space-y-4"
        >
          {/* Photo upload - highlighted tile */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-gradient-to-br from-[#1A1A1D] to-[#141416] border border-[#2A2A2D] p-5 rounded-lg hover:border-[#D4AF37]/30 hover:shadow-[0_0_20px_rgba(212,175,55,0.1)] transition-all duration-300"
          >
            <label className="block text-[#F5F5F5] text-sm mb-3 flex items-center gap-2 font-medium">
              <div className="w-8 h-8 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                <Camera className="w-4 h-4 text-[#D4AF37]" />
              </div>
              Zdjęcie Twojego psa
            </label>
            <div 
              className={`relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all duration-300 ${
                imagePreview ? "border-[#D4AF37]/50 bg-[#D4AF37]/5 shadow-[0_0_30px_rgba(212,175,55,0.15)]" : "border-[#2A2A2D] hover:border-[#D4AF37]/30 hover:bg-[#D4AF37]/5"
              }`}
              onClick={() => document.getElementById("photo-input").click()}
            >
              {imagePreview ? (
                <div className="relative">
                  <img src={imagePreview} alt="Preview" className="max-h-48 mx-auto rounded-lg shadow-lg" />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setImagePreview(null);
                      setFormData(prev => ({ ...prev, image_base64: "" }));
                    }}
                    className="absolute top-2 right-2 bg-red-500/80 text-white w-7 h-7 rounded-full text-sm hover:bg-red-500 shadow-lg"
                  >✕</button>
                </div>
              ) : (
                <>
                  <div className="w-14 h-14 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-3">
                    <Upload className="w-6 h-6 text-[#D4AF37]/50" />
                  </div>
                  <p className="text-[#B8B8B8] text-sm">Kliknij aby dodać zdjęcie</p>
                  <p className="text-[#B8B8B8]/40 text-xs mt-1">JPG, PNG (max 10MB)</p>
                </>
              )}
              <input 
                id="photo-input"
                type="file" 
                accept="image/*" 
                onChange={handleImageChange}
                className="hidden"
              />
            </div>
          </motion.div>

          {/* Dog info tile */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-[#1A1A1D] to-[#141416] border border-[#2A2A2D] p-5 rounded-lg hover:border-[#D4AF37]/30 hover:shadow-[0_0_20px_rgba(212,175,55,0.1)] transition-all duration-300"
          >
            <label className="block text-[#F5F5F5] text-sm mb-4 flex items-center gap-2 font-medium">
              <div className="w-8 h-8 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                <Dog className="w-4 h-4 text-[#D4AF37]" />
              </div>
              O Twoim psie
            </label>
            <div className="space-y-4">
              <div>
                <label className="block text-[#B8B8B8]/60 text-xs mb-1.5">Imię psa *</label>
                <input
                  type="text"
                  value={formData.dog_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, dog_name: e.target.value }))}
                  placeholder="np. Luna"
                  className="w-full bg-[#0B0B0D] border border-[#2A2A2D] px-4 py-3 rounded-lg text-[#F5F5F5] placeholder-[#B8B8B8]/30 focus:border-[#D4AF37]/50 focus:shadow-[0_0_15px_rgba(212,175,55,0.1)] focus:outline-none transition-all"
                />
              </div>
              <div>
                <label className="block text-[#B8B8B8]/60 text-xs mb-1.5">Z którego miotu (opcjonalnie)</label>
                <input
                  type="text"
                  value={formData.litter_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, litter_name: e.target.value }))}
                  placeholder="np. Miot M"
                  className="w-full bg-[#0B0B0D] border border-[#2A2A2D] px-4 py-3 rounded-lg text-[#F5F5F5] placeholder-[#B8B8B8]/30 focus:border-[#D4AF37]/50 focus:shadow-[0_0_15px_rgba(212,175,55,0.1)] focus:outline-none transition-all"
                />
              </div>
            </div>
          </motion.div>

          {/* Owner info tile */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-gradient-to-br from-[#1A1A1D] to-[#141416] border border-[#2A2A2D] p-5 rounded-lg hover:border-[#D4AF37]/30 hover:shadow-[0_0_20px_rgba(212,175,55,0.1)] transition-all duration-300"
          >
            <label className="block text-[#F5F5F5] text-sm mb-4 flex items-center gap-2 font-medium">
              <div className="w-8 h-8 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                <User className="w-4 h-4 text-[#D4AF37]" />
              </div>
              O Tobie
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[#B8B8B8]/60 text-xs mb-1.5">Twoje imię *</label>
                <input
                  type="text"
                  value={formData.owner_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, owner_name: e.target.value }))}
                  placeholder="np. Anna"
                  className="w-full bg-[#0B0B0D] border border-[#2A2A2D] px-4 py-3 rounded-lg text-[#F5F5F5] placeholder-[#B8B8B8]/30 focus:border-[#D4AF37]/50 focus:shadow-[0_0_15px_rgba(212,175,55,0.1)] focus:outline-none transition-all"
                />
              </div>
              <div>
                <label className="block text-[#B8B8B8]/60 text-xs mb-1.5">Miejscowość</label>
                <input
                  type="text"
                  value={formData.owner_city}
                  onChange={(e) => setFormData(prev => ({ ...prev, owner_city: e.target.value }))}
                  placeholder="np. Warszawa"
                  className="w-full bg-[#0B0B0D] border border-[#2A2A2D] px-4 py-3 rounded-lg text-[#F5F5F5] placeholder-[#B8B8B8]/30 focus:border-[#D4AF37]/50 focus:shadow-[0_0_15px_rgba(212,175,55,0.1)] focus:outline-none transition-all"
                />
              </div>
            </div>
          </motion.div>

          {/* Rating tile */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-br from-[#1A1A1D] to-[#141416] border border-[#2A2A2D] p-5 rounded-lg hover:border-[#D4AF37]/30 hover:shadow-[0_0_20px_rgba(212,175,55,0.1)] transition-all duration-300"
          >
            <label className="block text-[#F5F5F5] text-sm mb-4 flex items-center gap-2 font-medium">
              <div className="w-8 h-8 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                <Star className="w-4 h-4 text-[#D4AF37]" />
              </div>
              Twoja ocena
            </label>
            <div className="flex gap-3 justify-center py-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, rating: star }))}
                  className="p-1 transition-all hover:scale-125"
                >
                  <Star 
                    className={`w-10 h-10 transition-all drop-shadow-lg ${
                      star <= formData.rating 
                        ? "fill-[#D4AF37] text-[#D4AF37] drop-shadow-[0_0_10px_rgba(212,175,55,0.5)]" 
                        : "text-[#2A2A2D] hover:text-[#D4AF37]/30"
                    }`} 
                  />
                </button>
              ))}
            </div>
          </motion.div>

          {/* Review text tile */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="bg-gradient-to-br from-[#1A1A1D] to-[#141416] border border-[#2A2A2D] p-5 rounded-lg hover:border-[#D4AF37]/30 hover:shadow-[0_0_20px_rgba(212,175,55,0.1)] transition-all duration-300"
          >
            <label className="block text-[#F5F5F5] text-sm mb-4 flex items-center gap-2 font-medium">
              <div className="w-8 h-8 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                <MessageSquare className="w-4 h-4 text-[#D4AF37]" />
              </div>
              Twoja opinia *
            </label>
            <textarea
              value={formData.text}
              onChange={(e) => setFormData(prev => ({ ...prev, text: e.target.value }))}
              placeholder="Opisz swoje doświadczenia z hodowlą i jak Twój pies się odnalazł w nowym domu..."
              rows={5}
              className="w-full bg-[#0B0B0D] border border-[#2A2A2D] px-4 py-3 rounded-lg text-[#F5F5F5] placeholder-[#B8B8B8]/30 focus:border-[#D4AF37]/50 focus:shadow-[0_0_15px_rgba(212,175,55,0.1)] focus:outline-none transition-all resize-none"
            />
          </motion.div>

          {/* Error */}
          {error && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="p-4 bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-lg"
            >
              {error}
            </motion.div>
          )}

          {/* Submit */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            type="submit"
            disabled={submitting}
            className="w-full bg-gradient-to-r from-[#D4AF37] to-[#C4A030] text-[#0B0B0D] py-4 font-semibold rounded-lg hover:shadow-[0_0_30px_rgba(212,175,55,0.3)] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <div className="w-5 h-5 border-2 border-[#0B0B0D] border-t-transparent rounded-full animate-spin" />
                Wysyłanie...
              </>
            ) : (
              "Wyślij opinię"
            )}
          </motion.button>

          <p className="text-[#B8B8B8]/40 text-xs text-center mt-2">
            Twoja opinia zostanie opublikowana po weryfikacji
          </p>
        </motion.form>

        {/* Footer */}
        <div className="text-center mt-8 pb-4">
          <Link to="/" className="text-[#B8B8B8]/50 text-sm hover:text-[#D4AF37] transition-colors block mb-4">
            ← Wróć do strony głównej
          </Link>
          <p className="text-[10px] text-[#B8B8B8]/30">
            Made with love <a href="https://apisdesign.pl" target="_blank" rel="noopener noreferrer" className="hover:text-[#D4AF37] transition-colors">apisdesign.pl</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default KennelReviewForm;
